import { DefaultText } from "@/UI"
import DisplayPrice from "@/UI/DataDisplay/DisplayPrice"
import { currencyAtom } from "@/atoms/currencyAtom"
import { getSingleRuleCouriers } from "@/atoms/dispatchRulesAtom"
import { IDispatchRule, DispatchRuleType } from "@/types/dispatchRules.types"
import { getDeliveryTypeLabel } from "@/utils/delivery.utils"
import { vehicleImage } from "@/utils/providers"
import { faPen, faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { message, Card, Descriptions, Switch } from "antd"
import { capitalize } from "lodash"
import { useTranslation } from "react-i18next"
import { useState } from "react"
import { useRecoilValue } from "recoil"
import ProvidersList from "../../../ProvidersList/ProvidersList.container"
import DeleteRuleModal from "../DeleteRuleModal"
import UpdateRuleModal from "../UpdateRuleModal"
import Image from "next/image"
import { ProvidersType } from "@/types/providers.types"
import { assignmentTypeNames } from "@/types/draftOrder.types"
import { useDispatchRule } from "@/hooks/useDispatchRule"
import { useFlags } from "flagsmith/react"

const IconContainer: React.FC<{ children: React.ReactNode; onClick: () => void }> = ({
  children,
  onClick,
}) => {
  return (
    <div
      className="flex cursor-pointer item-center bg-[#F3F4F6] rounded-full p-2"
      onClick={onClick}
    >
      {children}
    </div>
  )
}
// TODO: Full and compact view could share the logic
const FullViewDispatchRuleCard: React.FC<{ rule: IDispatchRule }> = ({ rule }) => {
  const { t } = useTranslation()
  const flags = useFlags(["nash_branding"])
  const currency = useRecoilValue(currencyAtom)
  const [showProviders, setShowProviders] = useState<boolean>(false)

  const priorityMap = {
    [DispatchRuleType.ETA]: t("common.fastest"),
    [DispatchRuleType.PRICE]: t("common.cheapest"),
    [DispatchRuleType.RANK]: t("common.ranking"),
    [DispatchRuleType.ZONE]: t("common.zones"),
  }

  const handleRuleIdCopy = (ruleId: string) => {
    navigator.clipboard?.writeText(ruleId)
    message.success(t("common.copied"))
  }
  const { createDispatchRule, isLoading } = useDispatchRule()
  const [isUpdateModalVisible, setIsUpdateModalVisible] = useState<boolean>(false)
  const { name, ruleId, priority, maxPrice, deliveryType, vehicleType } = rule
  const ruleCouriers = useRecoilValue(getSingleRuleCouriers(rule?.id))
  const assignmentType = rule?.couriers?.find(
    (courier) => courier?.provider === ProvidersType.OWN_FLEET
  )?.assignmentType

  const onSwitchDispatchRule = (checked: boolean) => {
    createDispatchRule({ ...rule, disabled: !checked }, rule.id)
  }

  return (
    <Card key={rule.id} styles={{ body: { padding: "1.5em" } }} className="shadow-lg">
      <Descriptions
        title={
          <div className="flex space-x-1.5 items-center">
            <span>{name}</span>
            <span
              onClick={() => handleRuleIdCopy(ruleId)}
              className="text-sm text-gray-500 underline decoration-dotted cursor-pointer hover:bg-gray-50 p-1.5 rounded-md"
            >
              {ruleId}
            </span>
          </div>
        }
        size={"small"}
        extra={
          <div className="flex space-x-2">
            <div className="flex items-center">
              <Switch
                size="small"
                defaultChecked={!rule.disabled}
                onChange={onSwitchDispatchRule}
                loading={isLoading}
              />
            </div>
            <IconContainer onClick={() => setIsUpdateModalVisible(true)}>
              <FontAwesomeIcon className="text-gray-400" icon={faPen} />
            </IconContainer>
            <UpdateRuleModal
              rule={rule}
              isModalVisible={isUpdateModalVisible}
              closeModal={() => setIsUpdateModalVisible(false)}
            />
            <DeleteRuleModal ruleId={rule.id} />
          </div>
        }
        bordered
        labelStyle={{ whiteSpace: "nowrap", width: 200, padding: "6px 12px" }}
        contentStyle={{ padding: "6px 12px" }}
        column={1}
        className="dispatch-rules-description"
      >
        <Descriptions.Item label={t("profile.DispatchRules.priority")}>
          {capitalize(priorityMap[priority])}
        </Descriptions.Item>
        {!!maxPrice && (
          <Descriptions.Item label={t("profile.DispatchRules.maxDeliveryFee")}>
            <DisplayPrice amount={maxPrice} currencyCode={currency.code} justify="start" isCents />
          </Descriptions.Item>
        )}
        {!!deliveryType && (
          <Descriptions.Item label={t("common.order-field.deliveryType")}>
            {getDeliveryTypeLabel(deliveryType)}
          </Descriptions.Item>
        )}
        {!!vehicleType && (
          <Descriptions.Item label={t("common.order-field.vehicleType")}>
            <div className="flex items-center">
              <div className="shrink-0">
                <Image src={vehicleImage(vehicleType)} priority={true} width={24} height={24} />
              </div>
              <span className="ml-1">{capitalize(rule.vehicleType)}</span>
            </div>
          </Descriptions.Item>
        )}
        {!!assignmentType && (
          <Descriptions.Item label={t("common.assignmentType")}>
            {t(assignmentTypeNames[assignmentType])}
          </Descriptions.Item>
        )}
      </Descriptions>
      <div className="flex flex-col mt-3">
        <div className="flex space-x-3 ml-0.5">
          <DefaultText>{t("profile.DispatchRuleCard.amongProviders")}:</DefaultText>
          <div
            className="flex space-x-2 items-center cursor-pointer select-none"
            onClick={() => setShowProviders(!showProviders)}
          >
            <p
              className={
                showProviders
                  ? `text-[#70747D]`
                  : `text-${flags.nash_branding.enabled ? "nashPrimaryGreen" : "kosmoPrimaryBlue"}`
              }
            >
              {showProviders
                ? t("profile.DispatchRuleCard.hideProviders")
                : t("profile.DispatchRuleCard.seeProviders")}
            </p>
            <FontAwesomeIcon icon={showProviders ? faChevronUp : faChevronDown} color="#6B7280" />
          </div>
        </div>
        {showProviders && (
          <ProvidersList
            providers={ruleCouriers}
            type={rule.priority}
            className={`!mt-3 ${
              rule.priority === DispatchRuleType.RANK ? "w-full md:w-2/3" : "w-full"
            }`}
          />
        )}
      </div>
    </Card>
  )
}

export default FullViewDispatchRuleCard
